/**
 * Microbrand Atlas (MA) REST client.
 *
 * Read-only, browser-direct calls against MA's public Supabase project, using
 * the anon JWT their own site ships. Nothing here writes anywhere and nothing is
 * persisted — callers hold results in the session cache
 * (`components/ExternalBrandProposals/sessionCache.ts`).
 *
 * Three requests exist, and only three:
 *  - {@link fetchMaBrandsList}      two RPCs, once per tab open
 *  - {@link prefetchMaListColumns}  one GET, once per tab open (filter columns)
 *  - {@link fetchMaBrandDetail}     one GET per row click
 */
import type {
  MaBrandDetail,
  MaBrandRow,
  MaDiscoverRow,
  MaExploreRow,
  MaListColumnPrefetch,
} from './microbrandAtlas.types';

/** Thrown for a missing env var, so the UI can say "configure", not "retry". */
export class MaConfigError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'MaConfigError';
  }
}

/** A non-2xx from MA. Carries the status for the error banner. */
export class MaRequestError extends Error {
  readonly status: number;
  constructor(status: number, message: string) {
    super(message);
    this.name = 'MaRequestError';
    this.status = status;
  }
}

interface MaConfig {
  url: string;
  anonKey: string;
}

function readConfig(): MaConfig {
  const url = import.meta.env.VITE_MICROBRAND_ATLAS_URL?.trim();
  const anonKey = import.meta.env.VITE_MICROBRAND_ATLAS_ANON_KEY?.trim();
  if (!url || !anonKey) {
    throw new MaConfigError(
      'Microbrand Atlas is not configured. Set VITE_MICROBRAND_ATLAS_URL and ' +
        'VITE_MICROBRAND_ATLAS_ANON_KEY (see .env.example).',
    );
  }
  return { url: url.replace(/\/+$/, ''), anonKey };
}

/** True when both env vars are present — lets the page explain itself up front. */
export function isMicrobrandAtlasConfigured(): boolean {
  try {
    readConfig();
    return true;
  } catch {
    return false;
  }
}

async function maFetch<T>(path: string, init?: RequestInit): Promise<T> {
  const { url, anonKey } = readConfig();
  const response = await fetch(`${url}${path}`, {
    ...init,
    headers: {
      // MA's gateway wants the anon JWT in BOTH headers; either alone is a 401.
      apikey: anonKey,
      Authorization: `Bearer ${anonKey}`,
      Accept: 'application/json',
      ...(init?.body ? { 'Content-Type': 'application/json' } : {}),
      ...init?.headers,
    },
  });
  if (!response.ok) {
    throw new MaRequestError(
      response.status,
      `Microbrand Atlas responded ${response.status} ${response.statusText}`.trim(),
    );
  }
  return (await response.json()) as T;
}

function rpc<T>(name: string): Promise<T> {
  // An empty body returns the whole catalogue; there is no server pagination.
  return maFetch<T>(`/rest/v1/rpc/${name}`, { method: 'POST', body: '{}' });
}

/**
 * Union the two list projections by brand id. Both RPCs return the same brand
 * set, but the merge does not depend on that: a brand present in only one still
 * yields a row. Discover wins only where explore has nothing — explore is the
 * richer projection, discover's contribution is lat/lng and tag ids.
 */
export function mergeMaListRows(
  explore: MaExploreRow[],
  discover: MaDiscoverRow[],
): MaBrandRow[] {
  const byId = new Map<string, MaBrandRow>();
  for (const row of discover) byId.set(row.id, { ...row });
  for (const row of explore) {
    const existing = byId.get(row.id);
    if (!existing) {
      byId.set(row.id, { ...row });
      continue;
    }
    const merged: Record<string, unknown> = { ...existing };
    for (const [key, value] of Object.entries(row)) {
      if (value !== null && value !== undefined) merged[key] = value;
    }
    byId.set(row.id, merged as unknown as MaBrandRow);
  }
  return Array.from(byId.values()).sort((a, b) => a.name.localeCompare(b.name));
}

/** The full MA catalogue: `explore_brands_v1` ∪ `discover_brands_v1`. */
export async function fetchMaBrandsList(): Promise<MaBrandRow[]> {
  const [explore, discover] = await Promise.all([
    rpc<MaExploreRow[]>('explore_brands_v1'),
    rpc<MaDiscoverRow[]>('discover_brands_v1'),
  ]);
  return mergeMaListRows(explore, discover);
}

const PREFETCH_COLUMNS = [
  'slug',
  'ecommerce_platform',
  'is_shopify',
  'founder_name',
  'founder_managed',
  'instagram_url',
  'website_url',
].join(',');

/**
 * The filter columns for every published brand, in one request, so the platform
 * and founder-managed filters work without opening each row.
 */
export function prefetchMaListColumns(): Promise<MaListColumnPrefetch[]> {
  return maFetch<MaListColumnPrefetch[]>(
    `/rest/v1/brands?select=${PREFETCH_COLUMNS}&is_published=eq.true&limit=1000`,
  );
}

/**
 * One brand's full row. Resolves `null` when MA has no published brand at that
 * slug (the endpoint answers `[]`, not a 404).
 */
export async function fetchMaBrandDetail(slug: string): Promise<MaBrandDetail | null> {
  const rows = await maFetch<MaBrandDetail[]>(
    `/rest/v1/brands?select=*&slug=eq.${encodeURIComponent(slug)}&is_published=eq.true`,
  );
  return rows[0] ?? null;
}
